import React from "react";
import { Header, Footer, CTA, VideoCard, ModuleLockup } from "./App";
import { Bi } from "./lang";
import Seo from "./Seo";

function ModuleBadge({ kind }) {
  if (kind === "live") return <span className="badge badge--live"><span className="dot" />Live</span>;
  if (kind === "dev") return <span className="badge badge--dev"><span className="dot" /><Bi en="In development" de="In Entwicklung" /></span>;
  if (kind === "poc") return <span className="badge badge--poc"><span className="dot" />POC</span>;
  return null;
}

export default function CapabilitiesPage() {
  return (
    <>
      <Seo
        title="AI Business Suite – 5 KI-Module für ERP-Prozesse | JUMIBI"
        description="Fünf KI-Module auf einer Prozess-Schicht: Dokumentenautomatisierung, SAP-Integration und mehr. Standortübergreifend, live in Wochen statt Jahren."
      />
      <Header />
      <main>
        {/* PAGE HERO */}
        <section className="page-hero">
          <div className="container">
            <span className="eyebrow eyebrow--dark"><Bi en="The JUMIBI AI Business Suite" de="Die JUMIBI AI Business Suite" /></span>
            <h1 className="h1"><Bi en="Five modules. One process layer." de="Fünf Module. Eine Prozess-Schicht." /></h1>
            <p>
              <Bi
                en="Every module follows one schema — JUMIBI + a functional name — and loads onto the same platform. Each is an entry point into the three-stage value stack, never sold in isolation."
                de="Jedes Modul folgt einem Schema — JUMIBI + Funktionsbezeichnung — und lädt auf dieselbe Plattform. Jedes ist ein Einstiegspunkt in den dreistufigen Wertstapel, nie isoliert verkauft."
              />
            </p>
          </div>
        </section>

        {/* MODULES */}
        <section className="section section--ice">
          <div className="container" style={{ maxWidth: 1040 }}>

            {/* AP */}
            <article className="cap-module" id="ap">
              <div className="cap-banner">
                <ModuleLockup code="Accounts Payable" large />
                <ModuleBadge kind="live" />
              </div>
              <div className="cap-video">
                <VideoCard />
              </div>
              <div className="cap-body">
                <div className="desc">
                  <p>
                    <Bi
                      en="AI-based accounts-payable processing. Template-free extraction, multi-level approval workflow and posting straight into your ERP. JUMIBI AP replaces legacy OCR tools like tangro — which need per-supplier configuration and constant maintenance — and the fully manual invoice handling many mid-market sites still run inside SAP. Today it runs in production across 12 mailboxes and 12 teams."
                      de="KI-basierte Eingangsrechnungsverarbeitung. Template-freie Extraktion, mehrstufiger Approval-Workflow und Buchung direkt in Ihr ERP. JUMIBI AP ersetzt Legacy-OCR-Tools wie tangro — die Konfiguration pro Lieferant und ständige Wartung brauchen — und die vollständig manuelle Rechnungsbearbeitung, die viele Mittelstands-Standorte noch im SAP betreiben. Heute läuft es im Produktivbetrieb über 12 Mailboxen und 12 Teams."
                    />
                  </p>
                  <div className="pill-list">
                    <span className="pill">12 mailboxes</span><span className="pill">12 teams</span><span className="pill">formerly DocInsight</span>
                  </div>
                </div>
                <div className="cap-aside">
                  <div className="bestpractice">
                    <div className="bp-label"><Bi en="Best practice" de="Best Practice" /></div>
                    <p>
                      <Bi
                        en="Start with the highest-volume mailbox of a single entity. At one industrial site the AP roll-out freed two FTE and removed legacy maintenance within one cycle — then expanded to the next site."
                        de="Beginnen Sie mit der volumenstärksten Mailbox einer einzelnen Entity. An einem Industriestandort setzte der AP-Roll-out zwei FTE frei und beseitigte die Legacy-Wartung innerhalb eines Zyklus — dann Ausweitung auf den nächsten Standort."
                      />
                    </p>
                  </div>
                </div>
              </div>
            </article>

            {/* CAPTURE */}
            <article className="cap-module" id="capture">
              <div className="cap-banner">
                <ModuleLockup code="Capture" large />
                <ModuleBadge kind="live" />
              </div>
              <div className="cap-body">
                <div className="desc">
                  <p>
                    <Bi
                      en="AI capture and structured filing of physical and digital documents — shop-floor papers, production documents, delivery notes. Documents are recognised and filed once, so production staff no longer maintain data twice in paper and in the system."
                      de="KI-Erfassung und strukturierte Ablage physischer und digitaler Belege — Shopfloor-Papiere, Produktionsdokumente, Lieferscheine. Belege werden einmal erkannt und abgelegt, sodass Produktionsmitarbeiter Daten nicht doppelt in Papier und System pflegen."
                    />
                  </p>
                  <div className="pill-list"><span className="pill">Industrial site · DE</span><span className="pill">Industrial site · FR</span></div>
                </div>
                <div className="cap-aside">
                  <div className="bestpractice">
                    <div className="bp-label"><Bi en="Best practice" de="Best Practice" /></div>
                    <p>
                      <Bi
                        en="Deploy where paper is re-keyed — the shop floor. Capture the document at the point of origin and feed the structured data to the ERP, eliminating double entry entirely."
                        de="Dort einsetzen, wo Papier abgetippt wird — am Shopfloor. Erfassen Sie den Beleg am Entstehungsort und übergeben Sie die strukturierten Daten ans ERP — Doppelerfassung entfällt vollständig."
                      />
                    </p>
                  </div>
                </div>
              </div>
            </article>

            {/* E-INVOICING */}
            <article className="cap-module" id="einvoicing">
              <div className="cap-banner">
                <ModuleLockup code="E-Invoicing" large />
                <ModuleBadge kind="live" />
              </div>
              <div className="cap-body">
                <div className="desc">
                  <p>
                    <Bi
                      en="Electronic outbound invoicing with country-specific compliance — connected to the national e-invoicing platforms: Italian SDI, French PPF/PDP (Peppol-based), Serbian E-Faktura. SAP integration is built in-house by our own SAP developers. Additional countries scale without separate specialist vendors."
                      de="Elektronische Ausgangsrechnung mit länderspezifischer Compliance — angebunden an die nationalen E-Invoicing-Plattformen: italienisches SDI, französisches PPF/PDP (Peppol-basiert), serbisches E-Faktura. Die SAP-Integration entsteht in-house durch eigene SAP-Entwickler. Weitere Länder skalieren ohne separate Spezialanbieter."
                    />
                  </p>
                  <div className="pill-list"><span className="pill">Italy · SDI</span><span className="pill">France · PPF/PDP</span><span className="pill">Serbia · E-Faktura</span></div>
                </div>
                <div className="cap-aside">
                  <div className="bestpractice">
                    <div className="bp-label"><Bi en="Best practice" de="Best Practice" /></div>
                    <p>
                      <Bi
                        en="Switch on per country as a mandate lands. Each added country replaces a separate vendor contract worth roughly €85k over five years — without a new specialist integration."
                        de="Pro Land aktivieren, sobald eine Pflicht greift. Jedes weitere Land ersetzt einen separaten Anbietervertrag von rund 85.000 € über fünf Jahre — ohne neue Spezial-Integration."
                      />
                    </p>
                  </div>
                </div>
              </div>
            </article>

            {/* ARCHIVE */}
            <article className="cap-module" id="archive">
              <div className="cap-banner">
                <ModuleLockup code="Archive" large />
                <ModuleBadge kind="live" />
              </div>
              <div className="cap-body">
                <div className="desc">
                  <p>
                    <Bi
                      en="A platform-wide ERP archive — audit-proof storage, full-text search and ERP integration over API. One archiving layer for every entity on the platform, instead of separate per-system archives."
                      de="Ein plattform-weites ERP-Archiv — revisionssichere Ablage, Volltextsuche und ERP-Anbindung über API. Eine Archiv-Schicht für jede Entity auf der Plattform statt getrennter Einzel-Archive."
                    />
                  </p>
                  <div className="pill-list"><span className="pill"><Bi en="Live · platform-wide" de="Live · plattformweit" /></span></div>
                </div>
                <div className="cap-aside">
                  <div className="bestpractice">
                    <div className="bp-label"><Bi en="Best practice" de="Best Practice" /></div>
                    <p>
                      <Bi
                        en="Pair with JUMIBI AP from day one, so every posted invoice is audit-proof and searchable immediately — no separate archiving project later."
                        de="Von Anfang an mit JUMIBI AP koppeln, damit jede gebuchte Rechnung sofort revisionssicher und durchsuchbar ist — kein separates Archiv-Projekt später."
                      />
                    </p>
                  </div>
                </div>
              </div>
            </article>

            {/* ORDER INTAKE */}
            <article className="cap-module" id="order-intake">
              <div className="cap-banner">
                <ModuleLockup code="Order Intake" large />
                <ModuleBadge kind="live" />
              </div>
              <div className="cap-body">
                <div className="desc">
                  <p>
                    <Bi
                      en="AI order capture from incoming purchase orders — email, EDI or PDF — with automated order creation in the ERP over API. Beyond pure intake, it optimizes sales: it surfaces up- and cross-sell opportunities on every order, helping you sell more products. Live on orderintake.jumibi.de."
                      de="KI-Auftragserfassung aus eingehenden Bestellungen — E-Mail, EDI oder PDF — mit automatisiertem Anlegen im ERP über API. Über die reine Erfassung hinaus optimiert es den Vertrieb: erkennt bei jedem Auftrag Up- und Cross-Selling-Potenziale und hilft so, mehr Produkte zu verkaufen. Live auf orderintake.jumibi.de."
                    />
                  </p>
                  <div className="pill-list"><span className="pill">Live</span><span className="pill">Up- &amp; Cross-Selling</span><span className="pill">orderintake.jumibi.de</span></div>
                </div>
                <div className="cap-aside">
                  <div className="bestpractice">
                    <div className="bp-label"><Bi en="Best practice" de="Best Practice" /></div>
                    <p>
                      <Bi
                        en="Pilot on one inbound channel (email or EDI) first. Validate extraction accuracy on real purchase orders, then automate ERP creation channel by channel."
                        de="Zuerst auf einem Eingangskanal (E-Mail oder EDI) pilotieren. Extraktionsgenauigkeit an echten Bestellungen validieren, dann ERP-Anlage Kanal für Kanal automatisieren."
                      />
                    </p>
                  </div>
                </div>
              </div>
            </article>

            {/* ROADMAP */}
            <div className="card" style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 14 }}>
              <span className="eyebrow"><Bi en="On the roadmap" de="Auf der Roadmap" /></span>
              <div className="pill-list">
                <span className="pill">JUMIBI Master Data</span>
                <span className="pill">JUMIBI Procurement</span>
              </div>
            </div>
          </div>
        </section>

        <CTA />
      </main>
      <Footer />
    </>
  );
}
